import { useState } from "react";
import { ArrowRight, Bell, Mail, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const LABEL = { breakfast: "Breakfast", lunch: "Lunch", snack: "Snacks", dinner: "Dinner" };

export const MealPlanCard = ({ stats, onEditProfile }) => {
  const [sending, setSending] = useState(false);
  const plan = stats?.meal_plan;
  if (!plan) return null;

  const next = plan.meals.find((m) => m.meal_type === plan.next_meal);
  const catchUp = next ? Math.round(next.carried_in) : 0;
  const hasProfile = Boolean(stats.profile);

  const sendReminder = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/dinner");
      toast[data.sent ? "success" : "info"](
        data.sent ? `Reminder emailed to ${data.email}` : "Nothing to catch up on for dinner"
      );
    } catch {
      toast.error("Could not send the reminder email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-surface rounded-xl border border-[#E8E3D9] p-6 hover:shadow-md transition-shadow duration-200"
      data-testid="meal-plan-card"
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="mr-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-moss">Meal targets</p>
          <h3 className="mt-1 font-heading text-xl sm:text-2xl font-medium tracking-tight text-deep-olive">
            {hasProfile
              ? `${Math.round(stats.goals.calories)} kcal for your body`
              : "Set your daily target"}
          </h3>
        </div>
        <Button
          data-testid="edit-profile-button"
          onClick={onEditProfile}
          className="h-9 rounded-full bg-transparent px-4 text-sm text-stone-grey hover:bg-surface-hover hover:text-deep-olive transition-colors duration-200"
        >
          <UserCog className="mr-1.5 h-4 w-4" />
          {hasProfile ? "Edit body details" : "Add age, weight, height"}
        </Button>
      </div>

      {!hasProfile && (
        <p className="mt-3 text-sm text-stone-grey leading-relaxed" data-testid="no-profile-hint">
          Add your age, height, weight and activity level and we will work out your daily calories
          and split them across breakfast, lunch, snacks and dinner.
        </p>
      )}

      {hasProfile && (
        <p className="mt-2 text-sm text-stone-grey">
          BMR {Math.round(stats.profile.bmr)} kcal · maintenance{" "}
          {Math.round(stats.profile.tdee)} kcal · {stats.profile.activity_level} activity
        </p>
      )}

      {catchUp > 0 && next && (
        <div
          className="mt-4 flex items-start gap-3 rounded-lg border border-ochre/40 bg-ochre/15 px-4 py-3"
          data-testid="catch-up-banner"
        >
          <Bell className="mt-0.5 h-4 w-4 shrink-0 text-[#8A6220]" />
          <div className="mr-auto">
            <p className="font-heading text-sm font-medium text-deep-olive">
              Eat {catchUp} kcal extra at {LABEL[next.meal_type].toLowerCase()}
            </p>
            <p className="text-xs text-stone-grey">
              You came in under target earlier, so {LABEL[next.meal_type].toLowerCase()} is now{" "}
              {Math.round(next.target)} kcal instead of {Math.round(next.base_target)} kcal.
            </p>
          </div>
          <Button
            data-testid="send-reminder-button"
            onClick={sendReminder}
            disabled={sending}
            className="h-9 shrink-0 rounded-full bg-deep-olive px-4 text-xs text-sand hover:bg-forest transition-colors duration-200"
          >
            <Mail className="mr-1.5 h-3.5 w-3.5" />
            {sending ? "Sending…" : "Email me"}
          </Button>
        </div>
      )}

      <ul className="mt-5 space-y-2.5" data-testid="meal-plan-list">
        {plan.meals.map((m) => {
          const pct = m.target ? Math.min(100, (m.eaten / m.target) * 100) : 0;
          const isNext = m.meal_type === plan.next_meal;
          return (
            <li
              key={m.meal_type}
              data-testid={`plan-${m.meal_type}`}
              className={`rounded-lg border px-4 py-3 transition-colors duration-200 ${
                isNext ? "border-forest/40 bg-forest/5" : "border-[#E8E3D9] bg-sand"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-heading text-sm font-medium text-deep-olive">
                  {LABEL[m.meal_type]}
                </span>
                {m.carried_in > 0 && (
                  <span
                    className="rounded-full bg-ochre/25 px-2 py-0.5 text-[10px] font-medium text-[#8A6220]"
                    data-testid={`carry-badge-${m.meal_type}`}
                  >
                    +{Math.round(m.carried_in)} carried over
                  </span>
                )}
                <span className="ml-auto text-sm text-stone-grey">
                  <span className="font-medium text-deep-olive">{Math.round(m.eaten)}</span> /{" "}
                  {Math.round(m.target)} kcal
                </span>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#EAE5D9]">
                  <div
                    className={`h-full rounded-full ${
                      m.status === "over" ? "bg-terracotta" : "bg-moss"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {m.remaining > 0 ? (
                  <span className="flex items-center gap-1 text-xs text-stone-grey whitespace-nowrap">
                    {Math.round(m.remaining)} kcal to go
                    {isNext && <ArrowRight className="h-3 w-3" />}
                  </span>
                ) : (
                  <span className="text-xs text-moss whitespace-nowrap">target met</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
